import { Link } from "react-router-dom";
import { ArrowRight, Building2, Globe, Headset, Star, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import TitleBand from "../../components/TitleBand/TitleBand";
import CountUp from "../../components/CountUp/CountUp";
import CtaBand from "../../components/Home/CtaBand";
import ContactForm from "../../components/Forms/ContactForm";
import ChatWidget from "../../components/ChatWidget/ChatWidget";
import { useInView } from "../../util/useInView";
import { PageSeo } from "../../seo/Seo";
import {
  AREAS,
  AWARDS,
  CERTIFICATIONS,
  EXPERTISE_ICONS,
  INTRO_AWARDS,
  PARTNERS,
  PILLARS,
  PROCESS_ICONS,
  STATS,
} from "./SobreContent";
import "./Sobre.css";

type Point = { title: string; text: string };

const pad = (n: number) => String(n + 1).padStart(2, "0");
const reveal = (i: number) => ({ "data-reveal": true, style: { "--i": i } as React.CSSProperties });

function SectionHead({ id, eyebrow, title, text }: { id: string; eyebrow: string; title: string; text?: string }) {
  return (
    <div className="cz-about-head">
      <span className="cz-eyebrow">{eyebrow}</span>
      <h2 className="cz-about-h2" id={id}>
        {title}
      </h2>
      {text && <p className="cz-about-lead">{text}</p>}
    </div>
  );
}

// Quem somos: texto, prêmios, áreas e presença à esquerda; os números em grade 2 x 2 à direita, contando do zero
// quando aparecem.
function Intro() {
  const { t } = useTranslation();
  const [ref, state] = useInView<HTMLUListElement>(0.3);
  const k = "sobre_page.intro";

  return (
    <section className="cz-about-section cz-about-intro" aria-labelledby="cz-about-intro-title">
      <div className="cz-about-intro__text">
        <h2 className="cz-about-intro__title" id="cz-about-intro-title">
          {t(`${k}.title_lead`, "Quem")} <span className="cz-about-accent">{t(`${k}.title_accent`, "somos")}</span>
        </h2>
        <p className="cz-about-lead">{t(`${k}.text`)}</p>

        <ul className="cz-about-pills">
          {INTRO_AWARDS.map((award) => (
            <li key={award} className="cz-about-pill">
              <Star size={15} strokeWidth={2} aria-hidden="true" />
              {award}
            </li>
          ))}
        </ul>

        <div className="cz-about-areas">
          <span className="cz-eyebrow">{t(`${k}.areas_label`, "Onde atuamos")}</span>
          <ul className="cz-about-areas__list">
            {AREAS.map(({ key, Icon }) => (
              <li key={key} className="cz-about-area">
                <span className="cz-about-area__icon" aria-hidden="true">
                  <Icon size={14} strokeWidth={2} />
                </span>
                {t(`${k}.areas.${key}`)}
              </li>
            ))}
          </ul>
        </div>

        <p className="cz-about-presence">
          <Globe size={22} strokeWidth={1.7} aria-hidden="true" />
          {t(`${k}.presence`)}
        </p>
      </div>

      <ul ref={ref} className={`cz-about-stats${state}`}>
        {STATS.map(({ key, value, label }, i) => (
          <li key={key} className="cz-about-stat" {...reveal(i)}>
            {/* destaque rosa na diagonal da grade */}
            <strong className={`cz-about-stat__value${i % 3 === 0 ? " is-accent" : ""}`}>
              <CountUp value={t(`sobre_page.stats.${key}.value`, value)} state={state} />
            </strong>
            <span className="cz-about-stat__label">{t(`sobre_page.stats.${key}.label`, label)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// O que nos move: 4 pilares em cards.
function Pillars() {
  const { t } = useTranslation();
  const [ref, state] = useInView<HTMLUListElement>(0.2);

  return (
    <section className="cz-about-band" aria-labelledby="cz-about-pillars-title">
      <div className="cz-about-section">
        <SectionHead
          id="cz-about-pillars-title"
          eyebrow={t("sobre_page.pillars_eyebrow", "Nossos valores")}
          title={t("sobre_page.pillars_title", "O que nos move")}
        />
        <ul ref={ref} className={`cz-about-pillars${state}`}>
          {PILLARS.map(({ key, Icon, title, text }, i) => (
            <li key={key} className="cz-about-pillar" {...reveal(i)}>
              <div className="cz-about-pillar__top">
                <span className="cz-about-pillar__icon" aria-hidden="true">
                  <Icon size={24} strokeWidth={1.7} />
                </span>
                <span className="cz-outline-num cz-about-pillar__num" aria-hidden="true">{pad(i)}</span>
              </div>
              <h3 className="cz-about-pillar__title">{t(`sobre_page.pillars.${key}.title`, title)}</h3>
              <p className="cz-about-pillar__text">{t(`sobre_page.pillars.${key}.text`, text)}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// Como trabalhamos: a linha corre de etapa em etapa e cada uma acende quando a linha chega nela.
function Process() {
  const { t } = useTranslation();
  const raw = t("home.process.steps", { returnObjects: true });
  const steps = Array.isArray(raw) ? (raw as Point[]) : [];
  const [ref, state] = useInView<HTMLOListElement>(0.3);

  return (
    <section className="cz-about-section" aria-labelledby="cz-about-process-title">
      <SectionHead
        id="cz-about-process-title"
        eyebrow={t("sobre_page.process_eyebrow", "Do começo ao fim")}
        title={t("home.process.title", "Como trabalhamos")}
        text={t("home.process.text")}
      />
      <ol ref={ref} className={`cz-about-steps${state}`} style={{ "--count": steps.length } as React.CSSProperties}>
        {steps.map(({ title, text }, i) => {
          const Icon = PROCESS_ICONS[i % PROCESS_ICONS.length];
          return (
            <li key={title} className="cz-about-step" style={{ "--i": i } as React.CSSProperties}>
              <span className="cz-about-step__dot" aria-hidden="true">
                <Icon size={20} strokeWidth={1.8} />
              </span>
              <span className="cz-about-step__num" aria-hidden="true">{pad(i)}</span>
              <h3 className="cz-about-step__title">{title}</h3>
              <p className="cz-about-step__text">{text}</p>
            </li>
          );
        })}
      </ol>
      <p className="cz-about-tip">
        <span className="cz-about-tip__icon" aria-hidden="true">
          <Headset size={18} strokeWidth={1.8} />
        </span>
        {t("home.process.tip")}
      </p>
    </section>
  );
}

// Nossas expertises em mosaico: as duas primeiras maiores.
function Expertise() {
  const { t } = useTranslation();
  const raw = t("home.expertise.items", { returnObjects: true });
  const items = Array.isArray(raw) ? (raw as Point[]) : [];
  const [ref, state] = useInView<HTMLUListElement>(0.15);

  return (
    <section className="cz-about-band" aria-labelledby="cz-about-expertise-title">
      <div className="cz-about-section">
        <SectionHead
          id="cz-about-expertise-title"
          eyebrow={t("sobre_page.expertise_eyebrow", "Time especializado")}
          title={t("home.expertise.title", "Nossas expertises")}
          text={t("home.expertise.text")}
        />
        <ul ref={ref} className={`cz-about-expertise${state}`}>
          {items.map(({ title, text }, i) => {
            const Icon = EXPERTISE_ICONS[i % EXPERTISE_ICONS.length];
            return (
              <li key={title} className="cz-about-expert" {...reveal(i)}>
                <span className="cz-about-expert__icon" aria-hidden="true">
                  <Icon size={22} strokeWidth={1.7} />
                </span>
                <h3 className="cz-about-expert__title">{title}</h3>
                <p className="cz-about-expert__text">{text}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

// Reconhecimentos: os dois prêmios com foto e os parceiros estratégicos.
function Recognition() {
  const { t } = useTranslation();
  const [ref, state] = useInView<HTMLDivElement>(0.15);

  return (
    <section className="cz-about-section" aria-labelledby="cz-about-awards-title">
      <SectionHead
        id="cz-about-awards-title"
        eyebrow={t("sobre_page.awards_eyebrow", "Reconhecimentos")}
        title={t("home.awards_title", "Premiações e reconhecimentos")}
        text={t("home.awards_text")}
      />
      <div ref={ref} className={`cz-about-recognition${state}`}>
        <ul className="cz-about-awards">
          {AWARDS.map(({ key, brand, photo, to }, i) => (
            <li key={key} className="cz-about-award" {...reveal(i)}>
              <div className="cz-about-award__media">
                <img src={photo} alt={t(`home.awards.${key}.img_alt`)} loading="lazy" />
                <span className="cz-about-award__brand">
                  <Trophy size={14} strokeWidth={2} aria-hidden="true" />
                  {brand}
                </span>
              </div>
              <div className="cz-about-award__body">
                <h3 className="cz-about-award__title">{t(`home.awards.${key}.title`)}</h3>
                <p className="cz-about-award__text">{t(`home.awards.${key}.text`)}</p>
                {to && (
                  <Link to={to} className="cz-about-award__more">
                    {t(`home.awards.${key}.cta`, "Saiba mais")}
                    <ArrowRight size={15} aria-hidden="true" />
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="cz-about-partners" {...reveal(AWARDS.length)}>
          <span className="cz-case-label cz-about-partners__label">
            {t("home.strategic_partners", "Parceiros Estratégicos")}
          </span>
          <ul className="cz-about-partners__list">
            {PARTNERS.map(({ key, logo, alt }) => (
              <li key={key} className="cz-about-partner">
                <img src={logo} alt={alt} className={`cz-about-partner__logo cz-about-partner__logo--${key}`} />
                <p className="cz-about-partner__text">{t(`home.awards.${key}.text`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// Certificações do time: selo, nome oficial e nível.
function Certifications() {
  const { t } = useTranslation();
  const [ref, state] = useInView<HTMLUListElement>(0.15);

  return (
    <section className="cz-about-band" aria-labelledby="cz-about-certs-title">
      <div className="cz-about-section">
        <SectionHead
          id="cz-about-certs-title"
          eyebrow={t("sobre_page.certs_eyebrow", "Time certificado")}
          title={t("home.certifications_title", "Principais certificações")}
          text={t("sobre_page.certs_text", "Nossos especialistas têm as certificações oficiais do Google em nuvem, dados, Workspace e educação.")}
        />
        <ul ref={ref} className={`cz-about-certs${state}`}>
          {CERTIFICATIONS.map(({ name, level, badge }, i) => (
            <li key={name} className="cz-about-cert" {...reveal(i)}>
              <img src={badge} alt="" className="cz-about-cert__badge" loading="lazy" />
              <span className="cz-about-cert__name">{name}</span>
              <span className="cz-about-cert__level">{level}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// /sobre — mesmo padrão das páginas de solução e de cases: faixa do título com a grade, seções alternando branco e
// faixa clara, rótulo rosa + título forte e entrada em cascata dos blocos.
export default function Sobre() {
  const { t } = useTranslation();

  return (
    <div className="cz-page cz-scope cz-about-page">
      <PageSeo page="sobre" path="/sobre" />
      <Header />
      <main>
        <TitleBand
          title={
            <>
              {t("sobre_page.title_lead", "Sobre a")} <span className="cz-about-accent">Calriz</span>
            </>
          }
          icon={Building2}
        />
        <Intro />
        <Pillars />
        <Process />
        <Expertise />
        <Recognition />
        <Certifications />
        <CtaBand />
        <ContactForm />
      </main>
      <ChatWidget />
    </div>
  );
}
